/**
 * Audit Pack generation DTOs.
 *
 * Mirrors Prisma `AuditPackGeneration`. The merged "audit-ready compliance
 * pack" (cover + every generated document) is generated server-side and
 * persisted so it can be re-downloaded — mobile shows the generation history
 * and a presigned download link for each READY pack.
 */
import { z } from "zod";

export const AuditPackStatusSchema = z.enum(["PENDING", "READY", "FAILED"]);
export type AuditPackStatus = z.infer<typeof AuditPackStatusSchema>;

export const AuditPackGenerationSchema = z.object({
  id: z.string(),
  status: AuditPackStatusSchema,
  fileName: z.string().nullable(),
  documentCount: z.number(),
  missingDocs: z.array(z.string()),
  /** Presigned download URL — present only for a READY pack. */
  downloadUrl: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
});
export type AuditPackGeneration = z.infer<typeof AuditPackGenerationSchema>;

/**
 * Server-only strict variant of the response DTO (#2411 batch 1).
 *
 * The mobile app parses `AuditPackGenerationSchema` off the wire via
 * `safeParseOrWarn`, so it is deliberately NOT `.strict()` — an installed
 * build must tolerate a field the server adds later. The server's
 * `serialiseAuditPack` builds the payload field-by-field and `.parse()`s it
 * as an over-serialisation guard, which is where the strictness is
 * re-applied.
 */
export const AuditPackGenerationStrict = AuditPackGenerationSchema.strict();
